/** @jsxImportSource @emotion/react */

import React, {
  useContext,
  Fragment,
  useRef,
  useEffect,
  useState,
} from 'react';
import { keyframes } from '@emotion/react';
import { dequal } from 'dequal';

import ScrollManager from '../../components/scrollManager';
import { ScreenContext } from '../../contexts/screenContext';

import Part, { IPart } from './part';
import BottomLabel, { IBottomLabel } from './components/bottomLabel';
import ScreenDecoration from './components/screenDecoration';
import useAction, { IAction } from '../../hooks/useAction';
import BottomAction, { IBottomAction } from './components/bottomAction';
import useTheme from '../../hooks/useTheme';
import useSnowplowTracker, {
  IStructuredSnowplowEvent,
} from '../../hooks/useSnowplowTracker';
import { scrollToViewportBottom } from '../../utils/smoothScroll';
import BottomPanel, { IBottomPanel } from './components/bottomPanel';
import useRumTracker from '../../hooks/useRumTracker';
import useOverlay from '../../hooks/useOverlay';
import useNavigation from '../../hooks/useNavigation';
import useLanguage from '../../hooks/useLanguage';

export interface IScreen {
  parts: Array<IPart>;
  screenName: string;
  title?: string;
  center?: boolean;
  hiddenIds?: Array<string>;

  bgColor?: string;
  headerColor?: string;
  footerColor?: string;
  ignoreSafeArea?: boolean;

  headerDecoration?: {
    height: number;
    type: number;
    color: string;
  };

  onLoadAction?: IAction;

  topBar?: {
    blurredEdge: boolean;
    transparent: boolean;
  };

  topRightAction?: {
    action: IAction;
    image?: string;
    text: string;
  };

  bottomLabel?: IBottomLabel;
  bottomAction?: IBottomAction;
  bottomPanel?: IBottomPanel;

  reloadConfig?: {
    autoReloadIntervalMillis?: number;
    shouldReloadOnShow: boolean;
    silent: boolean;
    preserveHiddenIds: 'keep' | 'merge' | 'replace';
  };

  snowplowTracker?: IStructuredSnowplowEvent;

  webSocketUrl?: string;
}

interface IScreenProps {
  scrollBottomOnPartsChanged?: boolean;
  visibleNavbar?: boolean;
}

const Screen = React.memo(
  ({ scrollBottomOnPartsChanged, visibleNavbar }: IScreenProps) => {
    const [screenHasReloaded, setScreenHasReloaded] = useState(false);

    const { screen, onLoadActions, clearOnLoadActions } =
      useContext(ScreenContext);
    const { color, resolveColor } = useTheme();
    const language = useLanguage();
    const overlay = useOverlay();
    const navigation = useNavigation();

    const prevScreen = useRef<IScreen>();

    const snowplowTracker = useSnowplowTracker();
    const handleAction = useAction();

    const rumTracker = useRumTracker();

    const handleActionRef = useRef(handleAction);
    handleActionRef.current = handleAction;
    const clearOnLoadActionsRef = useRef(clearOnLoadActions);
    clearOnLoadActionsRef.current = clearOnLoadActions;
    const screenRef = useRef(screen);
    screenRef.current = screen;

    const injectTopPadding = visibleNavbar && screen?.topBar?.transparent;

    // Execute on-load actions whenever a new set arrives (covers both the lazy
    // first-mount case and subsequent updates via SET_EXTERNAL_ACTION / websocket).
    useEffect(() => {
      if (!onLoadActions?.length) {
        return;
      }
      onLoadActions.forEach((action) => {
        // Do not reload the screen multiple times if it also has a
        // reloadConfig.shouldReloadOnShow — that effect already handles the reload.
        if (
          action.type === 'reload' &&
          screenRef.current?.reloadConfig?.shouldReloadOnShow
        ) {
          return;
        }
        handleActionRef.current(action);
      });
      clearOnLoadActionsRef.current();
    }, [onLoadActions]);

    const fadeInAnimation = keyframes`
    0% {opacity: 0}
    100% {opacity: 1}
  `;

    useEffect(() => {
      const handler = (e: Event) => {
        const url = (e as CustomEvent<string>).detail;
        overlay.presentAlert({
          message: language.get('external-link-pending-message'),
          items: [
            {
              title: language.get('cancel'),
              isCancel: true,
              onClick: () => {
                navigation.pop();
              },
            },
            {
              title: language.get('ok'),
              onClick: () => {
                window.open(url, '_blank', 'noopener,noreferrer');
              },
            },
          ],
        });
      };
      window.addEventListener('external-link-pending', handler);
      return () => window.removeEventListener('external-link-pending', handler);
    }, [language, navigation, overlay]);

    useEffect(() => {
      // Screens can reload and potentially get a different Snowplow event.
      // Only track unique events.
      if (
        screen?.snowplowTracker &&
        !dequal(screen?.snowplowTracker, prevScreen.current?.snowplowTracker)
      ) {
        snowplowTracker.trackEvent(screen.snowplowTracker);
      }

      // Track screen view in Datadog RUM if screenName has changed
      if (
        screen?.screenName &&
        screen.screenName.replace(/^\//, '') !==
          prevScreen.current?.screenName?.replace(/^\//, '')
      ) {
        rumTracker.startView(screen.screenName.replace(/^\//, ''));
      }

      // If scrollBottomOnPartsChanged is provided as a parameter for the screen,
      // scroll to the bottom of the viewport whenever its parts change.
      if (
        scrollBottomOnPartsChanged &&
        !dequal(screen?.parts, prevScreen.current?.parts)
      ) {
        scrollToViewportBottom();
      }

      // Check if screen has changed and has been loaded
      // before updating reload state.
      if (
        screen &&
        (screen.screenName === prevScreen?.current?.screenName ||
          prevScreen?.current?.screenName === undefined)
      ) {
        setScreenHasReloaded(true);
      } else {
        setScreenHasReloaded(false);
      }

      prevScreen.current = screen;
    }, [screen, snowplowTracker, scrollBottomOnPartsChanged, rumTracker]);

    return (
      <Fragment>
        {screen && (
          <div
            aria-live={screenHasReloaded ? 'polite' : 'off'}
            key={screen.screenName}
            css={{
              position: 'relative',
              display: 'flex',
              flex: 1,
              flexDirection: 'column',
              backgroundColor:
                resolveColor(screen.bgColor) || color.LOCAL_WHITE,
              width: '100%',
              opacity: 0,
              animation: `${fadeInAnimation} 0.3s forwards`,
              animationDelay: '0.1s',
            }}
          >
            {/* Inject the top padding to compensate for a collapsed/transparent navbar */}
            {injectTopPadding && (
              <div
                css={{
                  height: 60,
                  backgroundColor:
                    resolveColor(screen.headerColor || screen.bgColor) ||
                    color.LOCAL_WHITE,
                }}
              />
            )}

            {/* Screen/header decoration */}
            <ScreenDecoration
              color={resolveColor(screen.headerDecoration?.color)}
              height={screen.headerDecoration?.height}
            />

            {/* Part container, stretches to fill full screen height (minus the navbar)
          in order to be be able to vertically center parts if needed. */}
            <div
              css={{
                display: 'flex',
                flex: 1,
                flexDirection: 'column',
                justifyContent: screen.center ? 'center' : 'flex-start',
                width: '100%',
              }}
            >
              {screen.parts.map((part: IPart, i: number) => (
                <Part key={i} data={part} />
              ))}

              {/* If the display is taller than the actual screen and a footerColor
            is provided, fill the remaining space with the given color.  */}
              {screen.footerColor && (
                <div
                  css={{
                    backgroundColor: resolveColor(screen.footerColor),
                    flex: 1,
                  }}
                />
              )}
            </div>

            {/* The BottomPanel, takes precedence over the BottomLabel and BottomAction. */}
            {screen.bottomPanel && <BottomPanel {...screen.bottomPanel} />}

            {/* The BottomLabel, takes precedence over the BottomAction. */}
            {screen.bottomLabel && !screen.bottomPanel && (
              <BottomLabel data={screen.bottomLabel} />
            )}

            {/* The BottomAction, rendered only if no BottomPanel and no BottomLabel is provided. */}
            {screen.bottomAction &&
              !screen.bottomPanel &&
              !screen.bottomLabel && (
                <BottomAction data={screen.bottomAction} />
              )}

            <ScrollManager />
          </div>
        )}
      </Fragment>
    );
  }
);

export default Screen;
